// app/javascript/controllers/form_controller.js
import { Controller } from "@hotwired/stimulus"

/**
 * Form validation controller that disables/enables submit buttons based on HTML5 field validity
 * and shows contextual error messages using Tippy tooltips.
 * 
 * This controller uses HTML5 validation attributes (required, pattern, min, max, etc.) to
 * determine if form fields are valid. The submit button is disabled until all fields pass
 * their HTML5 validation rules.
 *
 * Usage:
 * 1. Add the controller to your form:
 *    <%= form_with data: { controller: "form" } do |f| %>
 *
 * 2. Mark fields that need validation with data-form-target="field" AND HTML5 validation attributes:
 *
 *    Option A - Direct on form elements:
 *    <%= f.select :contact_id, options, { prompt: "Select Contact" },
 *                 data: { "form-target": "field" }, 
 *                 required: true %>
 *    
 *    <%= f.email_field :email, 
 *                      data: { "form-target": "field" },
 *                      required: true,
 *                      placeholder: "user@example.com" %>
 *    
 *    <%= f.number_field :quantity,
 *                       data: { "form-target": "field" },
 *                       required: true,
 *                       min: 1,
 *                       max: 100 %>
 *
 *    Option B - On wrapper elements (fieldset, div):
 *    <fieldset data-form-target="field">
 *      <input type="radio" name="payment_method" value="card" required>
 *      <input type="radio" name="payment_method" value="ach" required>
 *    </fieldset>
 *
 *    * The wrapper element IS NEEDED for radio button groups to work properly.
 *    * The wrapper element IS NOT NEEDED for other form elements.
 *
 * 3. Add the submit target directly to your submit button:
 *    <%= f.submit "Pay", data: { "form-target": "submit" } %>
 *
 * Features:
 * - Prerequisites: Check conditions before HTML5 validation
 *   <fieldset data-form-target="field"
 *             data-form-prerequisite-failed="true" // for instance <%= @payment_methods.empty? %>
 *             data-form-prerequisite-message="No payment methods available">
 *
 * - Field-specific validation messages:
 *   <%= f.select :contact_id, options, {},
 *                data: {
 *                  "form-target": "field",
 *                  "form-invalid-message": "Please select a contact"
 *                } %>
 *
 * - Automatic radio button group validation
 * - Shows tooltip on first invalid field only
 * - Cleans up tooltips when form becomes valid
 *
 * Note: When using Rails' data hash, you can use either:
 * - Symbols with underscores: data: { form_target: "field" } (Rails converts to data-form-target)
 * - Strings with hyphens: data: { "form-target" => "field" }
 */
export default class extends Controller {
  static targets = ["field", "submit"]

  // Lifecycle callbacks
  connect() {
    // Check form validity on connect to set initial button state
    this.toggleSubmit()
  }

  disconnect() {
    // Clean up tippy on disconnect
    // We need to remove the wrapper from the DOM
    this.removeTippy()
  }

  // Actions
  fieldChanged() {
    this.toggleSubmit()
  }

  toggleSubmit() {
    const failedPrerequisite = this.checkPrerequisites()

    if (failedPrerequisite) {
      // Prerequisites allow fields to fail validation before HTML5 validation runs
      // Example: payment method selection required before form can be submitted
      const message = failedPrerequisite.dataset.formPrerequisiteMessage
      this.disableButton(message)
      return
    }

    const firstInvalidField = this.findFirstInvalidField()

    if (firstInvalidField) {
      const message = firstInvalidField.dataset.formInvalidMessage || this.data.get("invalidMessage")
      this.disableButton(message)
    } else {
      this.enableButton()
    }
  }

  // Private methods

  get tippyWrapper() {
    // Disabled buttons can't show tooltips directly, so we need a wrapper div
    // to attach the Tippy instance to. This wrapper is created on-demand and
    // removed when the form becomes valid.

    // Check if immediate parent is already a tippy wrapper
    const parent = this.submitTarget.parentElement
    if (parent && parent.dataset.formTippyWrapper === "true") {
      return parent
    }

    // Create wrapper if it doesn't exist
    if (parent) {
      const wrapper = document.createElement('div')
      wrapper.dataset.formTippyWrapper = "true"

      // Insert wrapper and move submit button into it
      parent.insertBefore(wrapper, this.submitTarget)
      wrapper.appendChild(this.submitTarget)

      return wrapper
    }

    return null
  }



  removeTippy() {
    // Find wrapper by our data attribute
    const wrapper = this.submitTarget.parentElement
    if (wrapper && wrapper.dataset.formTippyWrapper === "true") {
      // Remove tippy attributes
      delete wrapper.dataset.controller
      delete wrapper.dataset.tippyContent

      // Move submit button back to original position and remove wrapper
      if (wrapper.parentElement) {
        wrapper.parentElement.insertBefore(this.submitTarget, wrapper)
        wrapper.remove()
      }
    }
  }

  checkPrerequisites() {
    return this.fieldTargets.find((field) => {
      return field.dataset.formPrerequisiteFailed === "true"
    })
  }

  enableButton() {
    this.submitTarget.disabled = false
    this.removeTippy()
  }

  disableButton(message) {
    this.submitTarget.disabled = true
    if (message) {
      this.showTippy(message)
    }
  }

  findFirstInvalidField() {
    return this.fieldTargets.find((field) => {
      // Check if the field itself is a form element
      if (field.matches('input, select, textarea')) {
        return !field.checkValidity()
      }

      // Otherwise, look for form elements inside the field
      const formElements = field.querySelectorAll('input, select, textarea')
      const radioInputs = Array.from(formElements).filter(el => el.type === 'radio')

      if (radioInputs.length > 0) {
        const radioValid = this.validateRadioGroups(radioInputs)
        if (!radioValid) return true
      }

      const nonRadioElements = Array.from(formElements).filter(el => el.type !== 'radio')
      const hasInvalidElement = nonRadioElements.some(el => !el.checkValidity())
      return hasInvalidElement
    })
  }

  validateRadioGroups(radioInputs) {
    // Radio buttons need special handling because HTML5 validation doesn't
    // properly validate radio groups - it only checks individual inputs
    const radioGroups = {}

    radioInputs.forEach(radio => {
      if (!radioGroups[radio.name]) {
        radioGroups[radio.name] = []
      }
      radioGroups[radio.name].push(radio)
    })

    for (const [name, radios] of Object.entries(radioGroups)) {
      const isRequired = radios.some(r => r.required)
      const hasSelection = radios.some(r => r.checked)
      if (isRequired && !hasSelection) {
        return false
      }
    }

    return true
  }

  showTippy(message) {
    const wrapper = this.tippyWrapper
    if (wrapper) {
      wrapper.dataset.controller = "single-tippy"
      wrapper.dataset.tippyContent = message
    }
  }
}
